// packages/web-ui/src/components/loading/GeneratingViewState.tsx
//
// The shared, design-approved "Blueprint skeleton" generating state for
// AI-raster surfaces. Consumed by BOTH render sites:
//   - the chat card `<IllustrativeImagePanel>` (inline-cards), and
//   - the Views-tab `<ViewRasterSurface>` (view-renderer).
//
// Two variants:
//   - `canvas`  — first generation, no prior image: a block-sized card with a
//     faint drafting grid + an inline SVG ghost diagram that "inks itself in"
//     (blocks stagger-fade, connectors draw via stroke-dashoffset), plus a
//     status strip (beacon, staged caption, honest asymptotic progress
//     hairline, elapsed readout, optional Cancel).
//   - `overlay` — regenerating over a visible prior image: an
//     absolutely-positioned frosted layer (the HOST positions it inside a
//     `position: relative` container) with a centered capsule holding a thin
//     activity ring + labels; the prior image stays visible beneath — chat is
//     never blocked.
//
// The component is PRESENTATIONAL: the host owns the elapsed ticking state
// (and freezes it under reduced motion). All animation lives in
// `generating-view-state.css`, gated under the `.gvs-animate` class the
// component only applies when `reducedMotion` is false, PLUS a
// `prefers-reduced-motion: reduce` media guard in the CSS.
//
// User-facing copy must NEVER contain "LLM".

import type { JSX } from 'react';
import './generating-view-state.css';

/** Accessible name for the whole generating region. */
export const GENERATING_ARIA_LABEL =
  'Generating visualization, usually takes about a minute';

/** Single static caption shown under reduced motion. */
const STATIC_CAPTION = 'Generating visualization';

/**
 * Shared frame dimensions of the `canvas` variant. The sibling timeout-backstop
 * rows that replace the skeleton in the SAME slot reuse these so the card never
 * changes size (no reflow) when the backstop fires.
 */
export const GENERATING_CANVAS_WIDTH = 480;
export const GENERATING_CANVAS_HEIGHT = 260;

/**
 * Staged caption derived from elapsed seconds: the skeleton narrates rough
 * phases of the generation without pretending to know real progress.
 */
export function stageCaption(elapsedSeconds: number): string {
  if (elapsedSeconds < 8) return 'Composing blocks';
  if (elapsedSeconds <= 30) return 'Routing connections';
  return 'Refining detail';
}

/**
 * Honest asymptotic progress fill — approaches but never fakes completion.
 * Capped at 0.9 so the hairline visibly never claims "done".
 */
export function progressFraction(elapsedSeconds: number): number {
  return Math.min(0.9, 1 - Math.exp(-elapsedSeconds / 35));
}

export interface GeneratingViewStateTestIds {
  root?: string;
  elapsed?: string;
  cancel?: string;
}

export interface GeneratingViewStateProps {
  /**
   * `canvas` = first generation, no prior image (block-sized card);
   * `overlay` = regenerating over a visible prior image (absolute inset-0
   * frosted layer — the host provides the positioned ancestor).
   */
  variant: 'canvas' | 'overlay';
  /** Elapsed generation time — the HOST owns the ticking state. */
  elapsedMs: number;
  /** When present, renders a quiet text-only Cancel affordance. */
  onCancel?: () => void;
  /** Static, fully-drawn rendering with zero animation when true. */
  reducedMotion: boolean;
  /** Pass-through test ids so both host sites keep their existing contracts. */
  testIds?: GeneratingViewStateTestIds;
}

/** One ghost skeleton block: frame, title divider, text stubs, edge ports. */
function GhostBlock(props: {
  index: 1 | 2 | 3 | 4;
  x: number;
  y: number;
  w: number;
  h: number;
  ports: ReadonlyArray<{ x: number; y: number }>;
}): JSX.Element {
  const { index, x, y, w, h, ports } = props;
  const frame = 'var(--color-taupe-500, #B3B3B3)';
  const fill = 'var(--color-cream-100, #F2F2F2)';
  const stub = 'var(--color-taupe-300, #DDDDDD)';
  return (
    <g className={`gvs-block gvs-b${String(index)}`}>
      <rect x={x} y={y} width={w} height={h} rx={2} fill={fill} stroke={frame} strokeWidth={1} />
      {/* Title divider */}
      <line x1={x + 8} y1={y + 16} x2={x + w - 8} y2={y + 16} stroke={frame} strokeWidth={1} />
      {/* Grey text-stub bars */}
      <rect x={x + 8} y={y + 24} width={w * 0.55} height={4} rx={1} fill={stub} />
      <rect x={x + 8} y={y + 33} width={w * 0.38} height={4} rx={1} fill={stub} />
      {/* 6x6 port squares on the edges */}
      {ports.map((p) => (
        <rect
          key={`${String(p.x)}-${String(p.y)}`}
          x={p.x}
          y={p.y}
          width={6}
          height={6}
          fill="var(--color-cream-50, #FFFFFF)"
          stroke={frame}
          strokeWidth={1}
        />
      ))}
    </g>
  );
}

/**
 * Inline SVG ghost diagram — 4 skeleton blocks + 4 orthogonal connectors
 * (pathLength=1 so the CSS dash draw-in is unit-normalised).
 */
function GhostDiagram(): JSX.Element {
  const ink = 'var(--color-taupe-900, #000000)';
  return (
    <svg
      viewBox="0 0 480 176"
      className="h-full w-full"
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      focusable="false"
    >
      <GhostBlock
        index={1}
        x={28}
        y={22}
        w={112}
        h={58}
        ports={[
          { x: 137, y: 48 },
          { x: 137, y: 59 },
        ]}
      />
      <GhostBlock
        index={2}
        x={200}
        y={14}
        w={124}
        h={66}
        ports={[
          { x: 197, y: 44 },
          { x: 321, y: 44 },
          { x: 259, y: 77 },
        ]}
      />
      <GhostBlock index={3} x={364} y={26} w={96} h={54} ports={[{ x: 361, y: 50 }]} />
      <GhostBlock
        index={4}
        x={196}
        y={112}
        w={132}
        h={50}
        ports={[
          { x: 259, y: 109 },
          { x: 193, y: 134 },
        ]}
      />
      <path
        className="gvs-connector gvs-c1"
        d="M 143 51 L 170 51 L 170 47 L 197 47"
        fill="none"
        stroke={ink}
        strokeWidth={1}
        pathLength={1}
      />
      <path
        className="gvs-connector gvs-c2"
        d="M 327 47 L 344 47 L 344 53 L 361 53"
        fill="none"
        stroke={ink}
        strokeWidth={1}
        pathLength={1}
      />
      <path
        className="gvs-connector gvs-c3"
        d="M 262 83 L 262 109"
        fill="none"
        stroke={ink}
        strokeWidth={1}
        pathLength={1}
      />
      <path
        className="gvs-connector gvs-c4"
        d="M 143 62 L 168 62 L 168 137 L 193 137"
        fill="none"
        stroke={ink}
        strokeWidth={1}
        pathLength={1}
      />
    </svg>
  );
}

function CancelButton(props: {
  onCancel: () => void;
  testId?: string;
  className?: string;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={props.testId}
      aria-label="Cancel image generation"
      onClick={props.onCancel}
      className={`gvs-cancel cursor-pointer whitespace-nowrap border-0 bg-transparent p-0 text-[10px] font-semibold uppercase tracking-wide text-taupe-700 hover:text-fg ${props.className ?? ''}`}
    >
      Cancel
    </button>
  );
}

export function GeneratingViewState(props: GeneratingViewStateProps): JSX.Element {
  const { variant, elapsedMs, onCancel, reducedMotion, testIds } = props;
  const elapsedSeconds = Math.floor(elapsedMs / 1000);
  const caption = reducedMotion ? STATIC_CAPTION : stageCaption(elapsedSeconds);
  const elapsedLabel = `${String(elapsedSeconds)}s · usually ~1 min`;
  const animate = !reducedMotion;

  if (variant === 'overlay') {
    return (
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        aria-label={GENERATING_ARIA_LABEL}
        data-testid={testIds?.root}
        className={`gvs-root gvs-overlay absolute inset-0 flex items-center justify-center bg-cream-50/60 backdrop-blur-sm ${
          animate ? 'gvs-animate' : ''
        }`}
      >
        <div
          className="gvs-capsule flex items-center gap-3 rounded-[4px] border border-taupe-500 bg-cream-50/70 shadow-md backdrop-blur"
          style={{ padding: '14px 20px' }}
        >
          {/* 28px thin activity ring: taupe track + rotating accent arc. */}
          <svg viewBox="0 0 28 28" className="h-7 w-7 shrink-0" aria-hidden="true" focusable="false">
            <circle
              cx={14}
              cy={14}
              r={12}
              fill="none"
              stroke="var(--color-taupe-300, #DDDDDD)"
              strokeWidth={1.5}
            />
            <circle
              className="gvs-ring-arc"
              cx={14}
              cy={14}
              r={12}
              fill="none"
              stroke="var(--color-accent-red, #BB0A30)"
              strokeWidth={1.5}
              pathLength={100}
              strokeDasharray="25 75"
              strokeLinecap="round"
            />
          </svg>
          <div className="flex flex-col gap-0.5">
            <span className="text-fg text-[10px] font-bold uppercase tracking-wide">
              Generating visualization
            </span>
            {!reducedMotion ? (
              <span
                key={caption}
                className="gvs-caption text-[10px] uppercase tracking-wide text-taupe-700"
              >
                {caption}
              </span>
            ) : null}
            <span
              data-testid={testIds?.elapsed}
              className="text-[11px] tabular-nums text-taupe-700"
            >
              {elapsedLabel}
            </span>
            {onCancel !== undefined ? (
              <CancelButton
                onCancel={onCancel}
                {...(testIds?.cancel !== undefined ? { testId: testIds.cancel } : {})}
                className="mt-1 self-start"
              />
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  // ── canvas variant — the block-sized Blueprint skeleton card ───────────
  const fillPct = `${(progressFraction(elapsedSeconds) * 100).toFixed(1)}%`;
  return (
    <div
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={GENERATING_ARIA_LABEL}
      data-testid={testIds?.root}
      className={`gvs-root shadow-sm flex flex-col rounded-[2px] border border-taupe-500 bg-cream-50 ${
        animate ? 'gvs-animate' : ''
      }`}
      style={{ width: GENERATING_CANVAS_WIDTH, height: GENERATING_CANVAS_HEIGHT, maxWidth: '100%' }}
    >
      {/* Drafting-grid canvas with the ghost diagram inking itself in. */}
      <div className="gvs-grid min-h-0 flex-1 overflow-hidden">
        <GhostDiagram />
      </div>
      {/* Status strip: beacon · staged caption · progress hairline · elapsed · cancel */}
      <div className="flex h-10 shrink-0 items-center gap-3 border-t border-taupe-300 px-3">
        <span className="gvs-beacon bg-accent-red h-[6px] w-[6px] shrink-0" aria-hidden="true" />
        <span
          key={caption}
          className="gvs-caption text-fg whitespace-nowrap text-[10px] font-bold uppercase tracking-wide"
        >
          {caption}
        </span>
        <span className="relative h-[2px] min-w-8 flex-1 bg-taupe-300" aria-hidden="true">
          <span
            className="gvs-progress-fill bg-accent-red absolute inset-y-0 left-0"
            style={{ width: fillPct }}
          />
        </span>
        <span
          data-testid={testIds?.elapsed}
          className="whitespace-nowrap text-[11px] font-semibold tabular-nums text-taupe-700"
        >
          {elapsedLabel}
        </span>
        {onCancel !== undefined ? (
          <CancelButton
            onCancel={onCancel}
            {...(testIds?.cancel !== undefined ? { testId: testIds.cancel } : {})}
          />
        ) : null}
      </div>
    </div>
  );
}
